/**
 * Mobile workspace drawer — Phase 9.
 *
 * Renders ONLY at <md (i.e., <768 px). Two parts stacked vertically:
 *  1. A sticky one-line summary bar above the chat composer.
 *  2. A bottom-sheet drawer that overlays chat when the bar is tapped.
 *
 * Drawer mechanics: focus trap while open, Escape + backdrop +
 * grab-handle close, no layout shift (CSS-transform slide).
 */

import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import {
  formatBottomBarSummary,
  type BottomBarState,
} from "@starz/shared/workspace/summary";

interface Props {
  children: ReactNode;
  /** Drives the bar's summary text. */
  summary: BottomBarState;
}

export function MobileWorkspaceDrawer({ children, summary }: Props) {
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  const summaryText = formatBottomBarSummary(summary);

  // Lock body scroll while the drawer is open so scrolling inside it
  // doesn't cascade to the chat panel underneath. Cleanly restores the
  // original overflow on close.
  useEffect(() => {
    if (!open) return;
    const originalOverflow = document.body.style.overflow;
    const originalOverscroll = document.body.style.overscrollBehavior;
    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "contain";
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.overscrollBehavior = originalOverscroll;
    };
  }, [open]);

  // Escape closes; focus traps inside the open drawer.
  useEffect(() => {
    if (!open) return;

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      } else if (e.key === "Tab" && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
          'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", onKey);
    // Move focus into the drawer when it opens.
    dialogRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        aria-controls={titleId}
        className="md:hidden sticky bottom-16 left-0 right-0 z-30 flex w-full items-center justify-between gap-3 border-t border-starz-gold/20 bg-white px-4 py-3 text-sm shadow-[0_-4px_12px_-8px_rgba(30,61,88,0.15)]"
      >
        <span className="truncate text-starz-charcoal">{summaryText}</span>
        <span className="shrink-0 text-xs font-semibold uppercase tracking-widest text-starz-gold">
          Project ▲
        </span>
      </button>

      {open && (
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          className="md:hidden fixed inset-0 z-40 flex flex-col bg-starz-off-white"
        >
          {/* Sticky header — title + close button stay pinned at the top
              regardless of how far the user scrolls within the panel. */}
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-starz-gold/15 bg-starz-off-white px-4 py-3">
            <h2
              id={titleId}
              className="font-serif text-lg font-semibold text-starz-navy"
            >
              Project workspace
            </h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="-mr-2 flex h-10 w-10 items-center justify-center rounded-full text-warm-gray hover:bg-starz-cream/40 hover:text-starz-navy"
            >
              ✕
            </button>
          </div>
          {/* Scrollable content area. overscroll-contain so reaching the
              top/bottom doesn't rubber-band back to scroll the page. */}
          <div className="flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 pb-24 pt-4">
            {children}
          </div>
        </div>
      )}
    </>
  );
}
